import { forwardRef, useMemo } from "react";

import Box, { BoxProps } from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";

import { mergeSx } from "@/common/helpers/mergeSx";
import { IconSize } from "@/common/types/mui";
import { DashboardIcon, DashboardIconProps } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { IconElement } from "@/icons/types";

export type DashboardIconBlockVibe = "brand" | "danger" | "warning" | "positive" | "neutral";

export type DashboardIconBlockProps = Omit<BoxProps, "children"> & {
  children: IconElement;
  vibe?: DashboardIconBlockVibe;
  size?: "small" | "medium" | "large";
  slotProps?: {
    icon?: Omit<DashboardIconProps, "ref" | "children">;
  };
};

export const DashboardIconBlock = forwardRef<HTMLDivElement, DashboardIconBlockProps>(
  ({ children, vibe = "neutral", size = "medium", slotProps, sx, ...rest }, ref) => {
    const theme = useTheme();

    const iconSize: IconSize = useMemo(() => {
      switch (size) {
        case "small":
          return "small";
        case "medium":
          return "medium";
        case "large":
          return "large";
      }
    }, [size]);

    const styles = mergeSx(
      {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 1,
        ...(size === "large" && {
          width: 36,
          height: 36,
        }),
        ...(size === "medium" && {
          width: 32,
          height: 32,
        }),
        ...(size === "small" && {
          width: 24,
          height: 24,
        }),
        ...(vibe === "brand" && {
          backgroundColor: theme.palette.surface.brand.quiet,
          color: theme.palette.text.brand.moderate,
        }),
        ...(vibe === "danger" && {
          backgroundColor: theme.palette.surface.danger.quiet,
          color: theme.palette.text.danger.moderate,
        }),
        ...(vibe === "warning" && {
          backgroundColor: theme.palette.surface.warning.quiet,
          color: theme.palette.text.warning.moderate,
        }),
        ...(vibe === "positive" && {
          backgroundColor: theme.palette.surface.positive.quiet,
          color: theme.palette.text.positive.moderate,
        }),
        ...(vibe === "neutral" && {
          backgroundColor: theme.palette.surface.neutral.quiet,
          color: theme.palette.text.neutral.moderate,
        }),
      },
      sx
    );

    return (
      <Box ref={ref} sx={styles} {...rest}>
        <DashboardIcon vibe="currentColor" size={iconSize} {...slotProps?.icon}>
          {children}
        </DashboardIcon>
      </Box>
    );
  }
);

DashboardIconBlock.displayName = "DashboardIconBlock";

type DashboardIconBlockSkeletonProps = {
  size?: "small" | "medium" | "large";
  sx?: BoxProps["sx"];
};

export const DashboardIconBlockSkeleton = ({ size = "medium", sx }: DashboardIconBlockSkeletonProps) => {
  const styles = mergeSx(
    {
      width: size === "large" ? 36 : size === "medium" ? 32 : 24,
      height: size === "large" ? 36 : size === "medium" ? 32 : 24,
    },
    sx
  );

  return <DashboardSkeleton variant="rounded" sx={styles} />;
};
